import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { Organisatie } from '../../models/organisatie/organisatie';
import { ListParams } from '../../models/listParams';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import OrganisatiesActionMenu from './OrganisatiesActionMenu';

import { organisatieService } from '../../services/organisaties.service';
import { pageSizeStorageKey } from '../../services/utils/localStorageKeys';
import { usePersistedPageSize } from '../../hooks/usePersistedPageSize';

interface OverzichtOrganisatiesProps {
  isZonderGebied: boolean; // Callback function to trigger refresh
}

const OverzichtOrganisaties: React.FC<OverzichtOrganisatiesProps> = ({ 
  isZonderGebied
}) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [organisaties, setOrganisaties] = useState<Organisatie[]>([]);

  const [pageIndex, setPageIndex] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize(pageSizeStorageKey('organisaties'));
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const fetchOrganisaties = useCallback(async () => {
    setLoading(true);
    try {
      const params: ListParams = {
        offset: pageIndex * pageSize, 
        limit: pageSize, 
      };

      const data = await organisatieService.getOrganisaties(params, isZonderGebied);

      setOrganisaties(data.items); // Assuming the API response is a JSON object containing array of items
      setTotalCount(data.count);
      setLoading(false);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  }, [pageIndex, pageSize, isZonderGebied, navigate]);

  const gridColumns = useMemo<(GridColDef<Organisatie> | GridActionsColDef<Organisatie>)[]>(() => {
    const baseColumns: (GridColDef<Organisatie> | GridActionsColDef<Organisatie>)[] = [
      { field: 'id', headerName: 'Id', flex: 2 },
      { field: 'displayName', headerName: 'Naam', flex: 3 },
      { field: 'organizationType', headerName: 'Type', flex: 2 },
    ];
  
    if (!isZonderGebied) {
      baseColumns.push(
        { field: 'name', headerName: 'Organisatie', flex: 2 },
        { field: 'validatePath', headerName: 'Pad', flex: 3 }
      );
    }
  
    baseColumns.push({
      field: 'actions',
      headerName: 'Acties',
      renderHeader: () => <></>,
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Organisatie>) => (
        <OrganisatiesActionMenu params={params} isZonderGebied={isZonderGebied} onOrganisatiesUpdate={fetchOrganisaties} />
      ),
    });
  
    return baseColumns;
  }, [fetchOrganisaties, isZonderGebied]);

  // Calculate the dynamic height
  // const rowHeight = 52; // Default row height in MUI DataGrid
  // const headerHeight = 56; // Default header height
  // const gridHeight = headerHeight + (organisaties.length * rowHeight);
  
  useEffect(() => { 
    setReady(true); 
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchOrganisaties();
  }, [ready, fetchOrganisaties]);

  const handleOpenDialog = (id: string) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedId(null);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      handleDelete(selectedId);
    }
    handleCloseDialog();
  };

  // event
  const handleDelete = async (organisatieId: string) => {
    if (!organisatieId) {
      console.error('Organisatie id is null');
      return;
    }

    try {
      setLoading(true);

      // Call the updateOrganisatie method with the current organisatie state
      const response = await organisatieService.deleteOrganisatie(organisatieId);

      if (response.ok) {
        //console.log('Organisatie deleted successfully:', response);
        fetchOrganisaties();
      }
    }
    catch (error) {
      // Handle error (e.g., show an error message)
      console.error('Error updating organisatie:', error);
    }
  }

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPaginationModel({ page, pageSize });
    setPageIndex(page);
    setPageSize(pageSize);
  };

  return (
    <>
      <div className="grid-responsive" style={{ display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={organisaties}
          columns={gridColumns}
          disableColumnFilter
          disableColumnSorting 
          disableRowSelectionOnClick
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                page: pageIndex,
                pageSize: pageSize
              }
            }
          }}
          onPaginationModelChange={onChangePagination}
          // localeText={{
          //   MuiTablePagination: {
          //     labelRowsPerPage: 'Aantal per pagina:',
          //   },
          // }}
          // style={{
          //   height: gridHeight,
          //   border: '1px solid #ccc', // Optional border
          // }}
          // checkboxSelection
          sx={{ border: 0 }}
        />
      </div>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </>
  );
};

export default OverzichtOrganisaties;
